// Global Search Component - Search users by username or account number
import { useState, useEffect, useRef } from 'react';
import { Search, User, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { useLanguage } from '@/contexts';
import { Input } from '@/components/ui/input';

export default function GlobalSearch() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search users
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/user/search?q=${encodeURIComponent(query)}`);
        setResults(response.data.users || []);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (user) => {
    // Navigate to user profile or show user details
    setIsOpen(false);
    setQuery('');
    // For now, navigate to groups page where user can be invited
    navigate(`/groups?invite=${user.user_id}`);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 flex items-center justify-center rounded-xl border border-border/50 bg-surface hover:bg-surface-2 hover:border-primary/30 transition-all"
        data-testid="global-search-btn"
      >
        <Search className="w-4 h-4 text-muted-foreground" />
      </button>

      {/* Search dropdown */}
      
        {isOpen && (
          <div
            
            
            
            
            className="absolute right-0 top-12 w-80 sm:w-96 bg-surface border border-border/50 rounded-2xl shadow-2xl overflow-hidden z-50"
          >
            {/* Search input */}
            <div className="p-3 border-b border-border/30">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("search_users_placeholder") || "Поиск по никнейму или номеру..."}
                  className="pl-10 pr-10 bg-surface-2/50 border-border/30 rounded-xl"
                  data-testid="global-search-input"
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : results.length > 0 ? (
                <div className="p-2">
                  {results.map((user) => (
                    <button
                      key={user.user_id}
                      onClick={() => handleSelect(user)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-surface-2 transition-colors text-left"
                      data-testid={`search-result-${user.user_id}`}
                    >
                      {/* Avatar */}
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-10 h-10 rounded-xl object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/30 to-violet-500/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      
                      {/* User info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {user.username && (
                            <span className="truncate">@{user.username}</span>
                          )}
                          {user.user_code && (
                            <span className="text-xs bg-surface-2 px-2 py-0.5 rounded-md font-mono">
                              {user.user_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : query.length >= 2 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <User className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>{t("no_users_found") || "Пользователи не найдены"}</p>
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  <p className="text-sm">{t("search_hint") || "Введите минимум 2 символа"}</p>
                </div>
              )}
            </div>
          </div>
        )}
      
    </div>
  );
}
